import {
  Button,
  CheckboxFormField,
  DatePickerFormField,
  Form,
  IFormRender,
  Icon,
  Label,
  Theme,
  type IDatePickerPayload,
} from '@busybusy/webapp-react-ui';
import { ArrowDownIcon } from 'assets/icons';
import classNames from 'classnames';
import CertificationPickerFormField from 'components/domain/certification/CertificationPickerFormField/CertificationPickerFormField';
import CostCodePickerFormField from 'components/domain/cost-code/CostCodePickerFormField/CostCodePickerFormField';
import EquipmentPickerFormField from 'components/domain/equipment/EquipmentPickerFormField/EquipmentPickerFormField';
import PositionFormField from 'components/domain/position/PositionFormField/PositionFormField';
import ProjectPickerFormField from 'components/domain/project/ProjectPickerFormField/ProjectPickerFormField';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import BasicPickerFormField from 'components/foundation/form-fields/BasicPickerFormField/BasicPickerFormField';
import { smartFilterDateRestrictionsData } from 'containers/bulletin/constants/constants';
import useOpenable from 'hooks/utils/useOpenable';
import { t } from 'i18next';
import { FunctionComponent } from 'react';
import { ClassName } from 'types/ClassName';
import { v_require } from 'utils/validations';

export interface IEmployeeSmartFilterFieldOptions {
  smartClockedInFilter: boolean;
  smartScheduledFilter: boolean;
  smartClockedOutFilter: boolean;
  positionFilter: boolean;
  certificationFilter: boolean;
}
export interface IEmployeeSmartFilterFormData {
  showClockedIn: boolean;
  showClockedOut: boolean;
  showScheduled: boolean;
  clockedInProjectId: string | null;
  clockedInCostCodeId: string | null;
  clockedInEquipmentId: string | null;
  scheduledProjectId: string | null;
  scheduledCostCodeId: string | null;
  scheduledEquipmentId: string | null;
  scheduledDateRestrictions: string | null;
  scheduledDate: IDatePickerPayload | null;
  positionId: string | null;
  certificationId: string | null;
}

export interface IEmployeeSmartFilterFormProps {
  className?: ClassName;
  filterOptions: IEmployeeSmartFilterFieldOptions;
  getFormData: () => IEmployeeSmartFilterFormData;
  onFormSubmit: (data: IEmployeeSmartFilterFormData) => void;
  buttonTitle: string;
  onClearFilters: () => void;
}

export const EmployeeSmartFilterForm: FunctionComponent<IEmployeeSmartFilterFormProps> = (props) => {
  const { className, filterOptions, getFormData, onFormSubmit, buttonTitle, onClearFilters } = props;
  const classes = classNames(className);

  const dateDetails = useOpenable();

  function renderForm(form: IFormRender<IEmployeeSmartFilterFormData>) {
    return (
      <>
        {filterOptions.positionFilter && (
          <div className="pb-5">
            <Label>{t('Position')}</Label>
            <div>
              <PositionFormField form={form} name="positionId" placeholder={t('')} />
            </div>
          </div>
        )}

        {filterOptions.certificationFilter && (
          <div className="pb-5">
            <Label>{t('Certification')}</Label>
            <div>
              <CertificationPickerFormField form={form} name="certificationId" placeholder={t('')} />
            </div>
          </div>
        )}

        {filterOptions.smartClockedInFilter && (
          <>
            <CheckboxFormField
              name="showClockedIn"
              form={form}
              label={
                <>
                  <Label>{t('Clocked In')}</Label>
                  <h4>
                    {t(
                      'Only show employees that are currently clocked in. Optionally filter by project, cost code, and/or equipment.'
                    )}
                  </h4>
                </>
              }
              disabled={form.state.data.showClockedOut || form.state.data.showScheduled}
            />
            {form.state.data.showClockedIn && (
              <div className="pb-5">
                <DashboardRibbon theme={Theme.LIGHT}>
                  <ProjectPickerFormField form={form} name="clockedInProjectId" placeholder={t('Project')} />
                  <CostCodePickerFormField
                    form={form}
                    name="clockedInCostCodeId"
                    placeholder={t('Cost Code')}
                    considerProjectSpecificCostCodes={false}
                  />
                  <EquipmentPickerFormField form={form} name="clockedInEquipmentId" placeholder={t('Equipment')} />
                </DashboardRibbon>
              </div>
            )}
          </>
        )}

        {filterOptions.smartClockedOutFilter && (
          <>
            <CheckboxFormField
              name="showClockedOut"
              form={form}
              label={
                <>
                  <Label>{t('Clocked Out')}</Label>
                  <h4>{t('Only show employees that are currently clocked out.')}</h4>
                </>
              }
              disabled={form.state.data.showClockedIn || form.state.data.showScheduled}
            />
          </>
        )}

        {filterOptions.smartScheduledFilter && (
          <>
            <CheckboxFormField
              name="showScheduled"
              form={form}
              label={
                <>
                  <Label>{t('Scheduled')}</Label>
                  <h4>
                    {t(
                      'Only show employees that are scheduled to work on a specific project, cost code, and/or equipment.'
                    )}
                  </h4>
                </>
              }
              onChange={(value) => {
                if (value) {
                  form.setData('scheduledDateRestrictions', 'anytime');
                }
              }}
              disabled={form.state.data.showClockedIn || form.state.data.showClockedOut}
            />
            {form.state.data.showScheduled && (
              <div className="pb-5">
                <DashboardRibbon theme={Theme.LIGHT}>
                  <ProjectPickerFormField form={form} name="scheduledProjectId" placeholder={t('Project')} />
                  <CostCodePickerFormField
                    form={form}
                    name="scheduledCostCodeId"
                    placeholder={t('Cost Code')}
                    considerProjectSpecificCostCodes={false}
                  />
                  <EquipmentPickerFormField form={form} name="scheduledEquipmentId" placeholder={t('Equipment')} />
                  <BasicPickerFormField
                    name="scheduledDateRestrictions"
                    form={form}
                    data={smartFilterDateRestrictionsData}
                    closeButtonRender={() => (
                      <span className="drop-icon">
                        <Icon svg={ArrowDownIcon} />
                      </span>
                    )}
                    validations={[{ validation: v_require }]}
                  />
                  {form.state.data.scheduledDateRestrictions === 'date' && (
                    <DatePickerFormField
                      name="scheduledDate"
                      form={form}
                      validations={[{ validation: v_require }]}
                      isOpen={dateDetails.isOpen}
                      onClose={dateDetails.close}
                      onOpen={dateDetails.open}
                      placeholder={'Select Date'}
                    />
                  )}
                </DashboardRibbon>
              </div>
            )}
          </>
        )}

        <Button type="primary" onClick={form.handleSubmit} className="m-2">
          {buttonTitle}
        </Button>
        <Button type="secondary" onClick={onClearFilters} className="m-2">
          {t('Clear Filters')}
        </Button>
      </>
    );
  }

  return (
    <div className={classes}>
      <Form data={getFormData()} onSubmit={onFormSubmit} render={renderForm} />
    </div>
  );
};
